import { BaseUniDriver } from '@wix/wix-ui-test-utils/base-driver';
import { baseUniDriverFactory } from '../../test-utils/utils/unidriver';
import { UniDriver } from '@wix/wix-ui-test-utils/unidriver';
import { dataHooks } from './constants';

export interface CircularProgressBarCoreUniDriver extends BaseUniDriver {
  /** Returns boolean that indicates if the success icon exists */
  isSuccessIconDisplayed(): Promise<boolean>;
  /** Returns boolean that indicates if the error icon exists */
  isErrorIconDisplayed(): Promise<boolean>;
  /** Returns boolean that indicates if the progress percentages text exists */
  isPercentagesProgressDisplayed(): Promise<boolean>;
  /** Returns boolean that indicates if the label text exists */
  isLabelDisplayed(): Promise<boolean>;
  /** Get the text content displayed */
  getLabelTextContent(): Promise<string>;
  /** Get the progress percentages value */
  getValue(): Promise<string | null>;
  /** Returns true if has progress completed (value is 100) */
  isCompleted(): Promise<boolean>;
  /** Returns true if has error */
  hasError(): Promise<boolean>;
  getAttribute(name: string): Promise<string | null>;
  /** Returns true if single element with given data-hook rendered*/
  isSingleElementRendered(dataHook: string): Promise<boolean>;
}

const byDataHook = (dataHook: string) => `[data-hook="${dataHook}"]`;

export const circularProgressBarCoreUniDriverFactory = (
  base: UniDriver,
): CircularProgressBarCoreUniDriver => {
  const getValue = async () => {
    if (!(await base.exists())) {
      return null;
    }

    return base.$(byDataHook(dataHooks.progressIndicator)).text();
  };

  return {
    ...baseUniDriverFactory(base),
    isSuccessIconDisplayed: () =>
      base.$(byDataHook(dataHooks.successIcon)).exists(),
    isErrorIconDisplayed: () =>
      base.$(byDataHook(dataHooks.errorIcon)).exists(),
    isLabelDisplayed: () => base.$(byDataHook(dataHooks.label)).exists(),
    getLabelTextContent: () => base.$(byDataHook(dataHooks.label)).text(),
    isPercentagesProgressDisplayed: () =>
      base.$(byDataHook(dataHooks.progressIndicator)).exists(),
    getValue: () => getValue(),
    isCompleted: async () => (await getValue()) === '100',
    hasError: async () => (await base.attr('data-error')) === 'true',
    getAttribute: async name => base.attr(name),
    isSingleElementRendered: async (dataHook: string) =>
      (await base.$$(byDataHook(dataHook)).count()) === 1,
  };
};
